import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface AccountingPreviewProps {
  currency: string;
  originalTotal: number;
  totalCreditedSoFar: number;
  thisAmount: number;
  newRemainingBalance: number;
  /** "credit" | "debit" to adjust copy */
  noteType: "credit" | "debit";
  /** Tax breakdown for the note being created. */
  taxBreakdown?: { subtotal: number; taxAmount: number; taxRate: number };
  className?: string;
}

function formatAmount(currency: string, amount: number): string {
  return `${currency} ${Number(amount).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function AccountingPreview({
  currency,
  originalTotal,
  totalCreditedSoFar,
  thisAmount,
  newRemainingBalance,
  noteType,
  taxBreakdown,
  className,
}: AccountingPreviewProps): JSX.Element {
  const { t } = useTranslation();

  const newBalanceClass =
    newRemainingBalance > 0
      ? "text-green-700 dark:text-green-400 font-semibold"
      : newRemainingBalance < 0
        ? "text-destructive font-semibold"
        : "text-muted-foreground";

  const thisLabel =
    noteType === "credit"
      ? t("invoices.notes.accountingPreview.thisCreditAmount", "This credit note amount")
      : t("invoices.notes.accountingPreview.thisDebitAmount", "This debit note amount");

  const afterCopy =
    noteType === "credit"
      ? t("invoices.notes.accountingPreview.afterCredit", "After issuing this Credit Note, the remaining balance will be:")
      : t("invoices.notes.accountingPreview.afterDebit", "After issuing this Debit Note, the remaining balance will be:");

  return (
    <Card className={cn("border-primary/30", className)}>
      <CardHeader>
        <CardTitle className="text-foreground text-base">
          {t("invoices.notes.accountingPreview.title", "Accounting preview")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[70%]">
                {t("invoices.notes.accountingPreview.concept", "Concept")}
              </TableHead>
              <TableHead className="text-right">
                {t("invoices.notes.accountingPreview.amount", "Amount")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("invoices.notes.accountingPreview.originalTotal", "Original total")}
              </TableCell>
              <TableCell className="text-right">
                {formatAmount(currency, originalTotal)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("invoices.notes.accountingPreview.totalCreditedSoFar", "Total credited so far")}
              </TableCell>
              <TableCell className="text-right">
                {formatAmount(currency, totalCreditedSoFar)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-foreground">{thisLabel}</TableCell>
              <TableCell className="text-right font-medium">
                {formatAmount(currency, thisAmount)}
              </TableCell>
            </TableRow>
            {taxBreakdown && taxBreakdown.taxRate > 0 ? (
              <>
                <TableRow>
                  <TableCell className="pl-6 text-muted-foreground text-xs">
                    {t("invoices.notes.accountingPreview.subtotal", "Subtotal (net)")}
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {formatAmount(currency, taxBreakdown.subtotal)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="pl-6 text-muted-foreground text-xs">
                    {t("invoices.notes.accountingPreview.tax", "IVA ({{rate}}%)", { rate: Math.round(taxBreakdown.taxRate * 100) })}
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {formatAmount(currency, taxBreakdown.taxAmount)}
                  </TableCell>
                </TableRow>
              </>
            ) : null}
            <TableRow>
              <TableCell className="font-medium text-foreground">
                {t("invoices.notes.accountingPreview.newRemainingBalance", "New remaining balance")}
              </TableCell>
              <TableCell className={cn("text-right", newBalanceClass)}>
                {formatAmount(currency, newRemainingBalance)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-sm text-muted-foreground">
          {afterCopy} <span className={cn("font-medium text-foreground", newBalanceClass)}>{formatAmount(currency, newRemainingBalance)}</span>.
        </p>
      </CardContent>
    </Card>
  );
}
